import React, {useContext, useEffect, useState} from 'react';
import {
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Grid,
    MenuItem,
    TextField,
    Alert,
} from '@mui/material';
import APIWrapperContext from '../../../../../services/APIWrapperContext';
import {
    InvoiceService,
    InvoiceProduct,
    InvoiceProductCreate,
    InvoiceProductUpdate,
} from '../../../../../services/invoiceService';

interface ProductDialogProps {
    open: boolean;
    product: InvoiceProduct | null;
    onClose: () => void;
    onSave: () => void;
}

// Common unit codes per UN/ECE Recommendation 20
const UNIT_CODES = [
    {code: 'HUR', label: 'Stunde'},
    {code: 'C62', label: 'Stück'},
    {code: 'KGM', label: 'Kilogramm'},
    {code: 'MTR', label: 'Meter'},
    {code: 'LTR', label: 'Liter'},
    {code: 'MTK', label: 'Quadratmeter'},
    {code: 'DAY', label: 'Tag'},
    {code: 'MON', label: 'Monat'},
];

// VAT category codes
const VAT_CATEGORIES = [
    {code: 'S', label: 'Standard'},
    {code: 'Z', label: 'Nullsatz'},
    {code: 'E', label: 'Steuerbefreit'},
    {code: 'AE', label: 'Umkehrung der Steuerschuld'},
    {code: 'K', label: 'Innergemeinschaftliche Lieferung'},
    {code: 'G', label: 'Ausfuhr'},
];

const ProductDialog: React.FC<ProductDialogProps> = ({open, product, onClose, onSave}) => {
    const {call_backend_api} = useContext(APIWrapperContext);
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    // Form fields
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [sellerItemId, setSellerItemId] = useState('');
    const [defaultPrice, setDefaultPrice] = useState<string>('');
    const [unitCode, setUnitCode] = useState('HUR');
    const [unitLabel, setUnitLabel] = useState('');
    const [vatCategoryCode, setVatCategoryCode] = useState('S');
    const [vatRate, setVatRate] = useState<string>('19');
    const [active, setActive] = useState(true);

    useEffect(() => {
        if (product) {
            setName(product.name);
            setDescription(product.description || '');
            setSellerItemId(product.seller_item_id || '');
            setDefaultPrice(product.default_price?.toString() || '');
            setUnitCode(product.unit_code);
            setUnitLabel(product.unit_label || '');
            setVatCategoryCode(product.vat_category_code);
            setVatRate(product.vat_rate.toString());
            setActive(product.active);
        } else {
            // Reset form for new product
            setName('');
            setDescription('');
            setSellerItemId('');
            setDefaultPrice('');
            setUnitCode('HUR');
            setUnitLabel('');
            setVatCategoryCode('S');
            setVatRate('19');
            setActive(true);
        }
        setError(null);
    }, [product, open]);

    const handleUnitCodeChange = (code: string) => {
        setUnitCode(code);
        // Auto-fill label if empty
        const unit = UNIT_CODES.find(u => u.code === code);
        if (unit && !unitLabel) {
            setUnitLabel(unit.label);
        }
    };

    const handleSave = () => {
        // Validation
        if (!name.trim()) {
            setError('Name ist erforderlich');
            return;
        }

        const parsedPrice = defaultPrice ? parseFloat(defaultPrice.replace(',', '.')) : undefined;
        if (defaultPrice && (isNaN(parsedPrice!) || parsedPrice! < 0)) {
            setError('Ungültiger Preis');
            return;
        }

        const parsedVatRate = parseFloat(vatRate.replace(',', '.'));
        if (isNaN(parsedVatRate) || parsedVatRate < 0 || parsedVatRate > 100) {
            setError('Ungültiger MwSt.-Satz (0-100)');
            return;
        }

        setSaving(true);
        setError(null);

        const data: InvoiceProductCreate | InvoiceProductUpdate = {
            name: name.trim(),
            description: description.trim() || undefined,
            seller_item_id: sellerItemId.trim() || undefined,
            default_price: parsedPrice,
            unit_code: unitCode,
            unit_label: unitLabel.trim() || undefined,
            vat_category_code: vatCategoryCode,
            vat_rate: parsedVatRate,
            active,
        };

        const apiCall = product
            ? InvoiceService.updateProduct(product.id, data)
            : InvoiceService.createProduct(data as InvoiceProductCreate);

        call_backend_api(
            apiCall,
            () => {
                setSaving(false);
                onSave();
            },
            (err: any) => {
                console.error('Error saving product:', err);
                setError('Fehler beim Speichern');
                setSaving(false);
            },
            undefined,
            false
        );
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                {product ? 'Produkt bearbeiten' : 'Neues Produkt erstellen'}
            </DialogTitle>
            <DialogContent>
                {error && (
                    <Alert severity="error" sx={{mb: 2, mt: 1}}>
                        {error}
                    </Alert>
                )}
                <Grid container spacing={2} sx={{mt: 0.5}}>
                    <Grid item xs={12}>
                        <TextField
                            label="Name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            fullWidth
                            required
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label="Beschreibung"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            fullWidth
                            multiline
                            rows={2}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Artikelnummer"
                            value={sellerItemId}
                            onChange={(e) => setSellerItemId(e.target.value)}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Standardpreis (EUR)"
                            value={defaultPrice}
                            onChange={(e) => setDefaultPrice(e.target.value)}
                            fullWidth
                            placeholder="0,00"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Einheit"
                            value={unitCode}
                            onChange={(e) => handleUnitCodeChange(e.target.value)}
                            fullWidth
                            select
                        >
                            {UNIT_CODES.map((unit) => (
                                <MenuItem key={unit.code} value={unit.code}>
                                    {unit.label} ({unit.code})
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Einheit (Anzeige)"
                            value={unitLabel}
                            onChange={(e) => setUnitLabel(e.target.value)}
                            fullWidth
                            placeholder="z.B. Std., Stk."
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="MwSt.-Kategorie"
                            value={vatCategoryCode}
                            onChange={(e) => setVatCategoryCode(e.target.value)}
                            fullWidth
                            select
                        >
                            {VAT_CATEGORIES.map((cat) => (
                                <MenuItem key={cat.code} value={cat.code}>
                                    {cat.label} ({cat.code})
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="MwSt.-Satz (%)"
                            value={vatRate}
                            onChange={(e) => setVatRate(e.target.value)}
                            fullWidth
                            placeholder="19"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={active}
                                    onChange={(e) => setActive(e.target.checked)}
                                />
                            }
                            label="Aktiv"
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>
                    Abbrechen
                </Button>
                <Button onClick={handleSave} variant="contained" disabled={saving}>
                    {saving ? 'Speichern...' : 'Speichern'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ProductDialog;
